<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useDocumentUploads } from '@/composables/useDocumentUploads';

import DSButton from 'primevue/button';
import Message from 'primevue/message';
import UserList from '@/pages/users/components/UserList.vue';
import UserOwnerVerificationModal from '@/pages/users/components/UserOwnerVerificationModal.vue';

import enCopy from '@/lang/general/en-us.json';
import { RouteNames } from '@/data/routes';
import { userHasRequiredFieldsForVerification } from '@/utils/user-helper';

// -----
// Setup
// -----

const store = useStore();
const router = useRouter();
const {
  hasPassedBusinessVerification,
  hasBusinessUploadedRequiredVerificationDocs,
  hasUserUploadedRequiredVerificationDocs,
  hasSoftFailedBusinessVerification,
  hasHardFailedBusinessVerification,
} = useDocumentUploads();

// ---------
// Variables
// ---------

const modalOwnerVerificationRef = ref(null);

// ------------
// Misc Methods
// ------------

const errorMessage = computed(() => {
  if (!store.getters.hasSignatory) {
    return enCopy.components.signatoryErrorMsg;
  }
  return undefined;
});

// Determine whether to show Next button or Verify button
const shouldShowNextButton = computed(() => {
  return (
    !store.getters.isLexisNexisDDPEnabled ||
    hasPassedBusinessVerification.value ||
    hasBusinessUploadedRequiredVerificationDocs.value ||
    hasUserUploadedRequiredVerificationDocs.value
  );
});

const hasRequiredFieldsForVerification = computed(() => {
  const eligibleOwners = store.getters.eligibleOwners || [];
  return eligibleOwners.some((owner) =>
    userHasRequiredFieldsForVerification(owner),
  );
});

function goBack() {
  router.push({
    name: RouteNames.PROFILE.INDEX,
  });
}

function toggleOwnerVerificationModal() {
  modalOwnerVerificationRef.value?.toggleModal();
}

function handleVerifyClick() {
  if (!store.getters.businessVerification) {
    router.push({
      name: RouteNames.VERIFICATION.VERIFYING,
    });
    return;
  }

  if (hasSoftFailedBusinessVerification.value) {
    router.push({
      name: RouteNames.VERIFICATION.INFO,
    });
    return;
  }

  if (hasHardFailedBusinessVerification.value) {
    router.push({
      name: RouteNames.VERIFICATION.DOCUMENTS,
    });
    return;
  }

  router.push({
    name: RouteNames.VERIFICATION.VERIFYING,
  });
}
</script>

<template>
  <div>
    <Message
      v-if="errorMessage"
      severity="error"
      :closable="false"
      :class="$style['page-message']"
    >
      <p>{{ errorMessage }}</p>
    </Message>

    <Message
      v-if="store.getters.businessHasPendingBillContractOrActiveAdvance"
      severity="info"
      :closable="false"
      :class="$style['page-message']"
    >
      <p>
        Signatory updates can't be made after funding or a contract is pending.
        Please contact our support team at +1 (415) 610-5166 (9AM-6PM ET) or
        <a href="mailto:support@clearco.com">support@clearco.com</a> for
        assistance.
      </p>
    </Message>

    <div :class="$style['owners-wrapper']">
      <UserList />
      <div :class="$style.actions">
        <DSButton
          label="Back"
          class="p-button-outlined"
          :class="$style.actions__button"
          @click="goBack"
        />
        <DSButton
          v-if="shouldShowNextButton"
          label="Next"
          :class="$style.actions__button"
          :disabled="!store.getters.hasSignatory"
          @click="toggleOwnerVerificationModal"
        />

        <DSButton
          v-else
          label="Verify"
          :class="$style.actions__button"
          :disabled="!hasRequiredFieldsForVerification"
          @click="handleVerifyClick"
        />
      </div>
    </div>
  </div>

  <UserOwnerVerificationModal ref="modalOwnerVerificationRef" />
</template>

<style lang="less" module scoped>
.page-message {
  margin: 24px 0 40px;
}

.owners-wrapper {
  max-width: 556px;
  margin: 0 auto;
}

.actions {
  margin-top: 40px;
  display: flex;
  gap: 16px;
}

.actions__button {
  width: 100%;
}
</style>
